import {
	addDays,
	dayIndexOf,
	formatCalendarDate,
	isValidCalendarDate,
	parseCalendarDate,
} from '@lib/datetime'
import { CreateTripDraft, totalDays, tripName } from './draft'

/**
 * Every label the create-trip screens derive from a draft. Pure and UTC-stable
 * (via the calendarDate helpers) so the copy is unit-testable; mirrors the iOS
 * flow's `CreateTripPresentation` so the clients read the same.
 */

export type CreateTripStep = 'where' | 'when' | 'name'

export const STEPS: CreateTripStep[] = ['where', 'when', 'name']

/** Content heading for a step. */
export const stepTitle = (step: CreateTripStep): string => {
	switch (step) {
		case 'where':
			return 'Where are you going?'
		case 'when':
			return 'When does it start?'
		case 'name':
			return 'Name your trip'
	}
}

/** Step-indicator label for a step. */
export const stepLabel = (step: CreateTripStep): string => {
	switch (step) {
		case 'where':
			return 'Destinations'
		case 'when':
			return 'Start Date'
		case 'name':
			return 'Name It'
	}
}

/**
 * How a step reads in the indicator, mirroring iOS: behind the current step
 * it is completed or skipped depending on whether its work got done; the last
 * step is reached by finishing the flow, never by jumping, so it is
 * unreachable rather than upcoming.
 */
export type StepIndicatorState = 'active' | 'completed' | 'skipped' | 'upcoming' | 'unreachable'

/** Whether a step's own work is done — what separates completed from skipped. */
const isSatisfied = (draft: CreateTripDraft, step: CreateTripStep): boolean => {
	switch (step) {
		case 'where':
			return draft.destinations.length > 0
		case 'when':
			return draft.startDate !== null
		case 'name':
			return true
	}
}

export interface StepPosition {
	/** The step's place in its flow. */
	index: number
	/** The step being shown. */
	currentIndex: number
	isLast: boolean
	isSatisfied: boolean
}

/**
 * The indicator rule itself, flow-agnostic (iOS `StepState.init`): the create
 * flow and the edit flow both read their steps through it.
 */
export const stepState = ({
	index,
	currentIndex,
	isLast,
	isSatisfied,
}: StepPosition): StepIndicatorState => {
	if (index === currentIndex) return 'active'
	if (isLast) return 'unreachable'
	if (index > currentIndex) return 'upcoming'
	return isSatisfied ? 'completed' : 'skipped'
}

export const stepIndicatorState = (
	draft: CreateTripDraft,
	step: CreateTripStep,
	current: CreateTripStep,
): StepIndicatorState => {
	const index = STEPS.indexOf(step)
	return stepState({
		index,
		currentIndex: STEPS.indexOf(current),
		isLast: index === STEPS.length - 1,
		isSatisfied: isSatisfied(draft, step),
	})
}

/** Tapping any step but the last jumps to it, like iOS. */
export const canJumpTo = (state: StepIndicatorState): boolean =>
	state !== 'active' && state !== 'unreachable'

/** The header's back chevron: every step but the first. */
export const showsHeaderBack = (step: CreateTripStep): boolean => step !== 'where'

// MARK: Labels

/** "1 day" / "9 days" */
export const daysLabel = (days: number): string => (days === 1 ? '1 day' : `${days} days`)

/** Past three the line stops reading as a list of places and starts reading as a wall. */
const MAX_NAMES_LISTED = 3

/**
 * The destinations as one line: "Hong Kong", "Hong Kong and Thailand",
 * "Japan, Kyoto and Toronto", then "Japan, Kyoto, Toronto + 2 more" past
 * three (iOS fits the count to its label width; the web lists a fixed three).
 * "New Trip" while there are none.
 */
export const joinedNames = (draft: CreateTripDraft): string => {
	const names = draft.destinations.map(destination => destination.place.name)
	if (names.length === 0) return 'New Trip'
	if (names.length === 1) return names[0]
	if (names.length > MAX_NAMES_LISTED) {
		return `${names.slice(0, MAX_NAMES_LISTED).join(', ')} + ${names.length - MAX_NAMES_LISTED} more`
	}
	return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** "Mon, Aug 17, 2026", or null while undated. */
export const startDateLabel = (draft: CreateTripDraft): string | null => {
	const start = parseCalendarDate(draft.startDate)
	return start ? `${formatCalendarDate(draft.startDate, 'weekday')}, ${start.year}` : null
}

/**
 * The whole trip as one line — "Mon, Aug 17 - 23, 2026" inside a month,
 * "Mon, Aug 28 - Sep 3, 2026" across two. A trip that runs over New Year
 * spells out both years, since one trailing year would misdate the start.
 * null while undated; a trip with no places yet spans its start day alone.
 * `days` defaults to the destinations' total; the edit flow passes the whole
 * trip's length, uncovered days included.
 */
export const tripDateRange = (
	draft: CreateTripDraft,
	days: number = totalDays(draft),
): string | null => {
	const from = parseCalendarDate(draft.startDate)
	if (!from) return null
	days = Math.max(days, 1)
	const end = addDays(draft.startDate, days - 1)
	const to = parseCalendarDate(end)
	if (!to) return null

	const lead = formatCalendarDate(draft.startDate, 'weekday')
	if (days <= 1) return `${lead}, ${to.year}`
	if (from.year !== to.year) {
		return `${lead}, ${from.year} - ${formatCalendarDate(end, 'monthDay')}, ${to.year}`
	}
	const tail = from.month === to.month ? String(to.day) : formatCalendarDate(end, 'monthDay')
	return `${lead} - ${tail}, ${to.year}`
}

/** "9 days · Mon, Aug 17 - 25, 2026", or the day count alone while undated. */
export const daysSummary = (draft: CreateTripDraft, days: number = totalDays(draft)): string => {
	const count = daysLabel(days)
	const range = tripDateRange(draft, days)
	return range ? `${count} · ${range}` : count
}

/** "«Trip name» is ready" */
export const inviteTitle = (draft: CreateTripDraft): string => `${tripName(draft)} is ready`

// MARK: Calendar

/**
 * Index of the destination that owns the zero-based trip day, or null when the
 * day falls outside the trip. The index feeds the alternating range tint.
 */
export const segmentColorIndex = (draft: CreateTripDraft, day: number): number | null => {
	if (day < 0) return null
	let bound = 0
	for (let index = 0; index < draft.destinations.length; index++) {
		bound += draft.destinations[index].dayCount
		if (day < bound) return index
	}
	return null
}

export interface CalendarDayState {
	/**
	 * Which destination's tint the cell takes — even/odd alternate. null → the
	 * neutral tint of a day no destination owns (only the edit flow has those).
	 */
	colorIndex: number | null
	/** The day the pick landed on (iOS parity); the web draws it like any other day of its range. */
	isStart: boolean
}

/** What owns a zero-based trip day. */
type DayCoverage = { kind: 'destination'; colorIndex: number } | { kind: 'uncovered' } | { kind: 'outside' }

const coverage = (draft: CreateTripDraft, day: number, uncoveredDays: number): DayCoverage => {
	const colorIndex = segmentColorIndex(draft, day)
	if (colorIndex !== null) return { kind: 'destination', colorIndex }
	const covered = totalDays(draft)
	if (day >= covered && day < covered + uncoveredDays) return { kind: 'uncovered' }
	return { kind: 'outside' }
}

/**
 * Tint for a calendar cell (dashed date), or null when the day is outside the
 * trip. The start day is always marked, even with no places yet.
 * `uncoveredDays` are trailing trip days no destination owns — the edit flow
 * shows them after the destination ranges in the neutral tint.
 */
export const calendarDayState = (
	draft: CreateTripDraft,
	date: string,
	uncoveredDays = 0,
): CalendarDayState | null => {
	// dayIndexOf answers 0 for invalid input — indistinguishable from the start
	// day — so a malformed startDate must bail out before the offset is read.
	if (!draft.startDate || !isValidCalendarDate(draft.startDate)) return null
	const offset = dayIndexOf(date, draft.startDate)
	const owner = coverage(draft, offset, uncoveredDays)
	if (offset === 0) {
		// No days at all (create flow before any place): still mark the pick in
		// the first segment colour.
		const colorIndex =
			owner.kind === 'destination' ? owner.colorIndex : owner.kind === 'uncovered' ? null : 0
		return { colorIndex, isStart: true }
	}
	switch (owner.kind) {
		case 'destination':
			return { colorIndex: owner.colorIndex, isStart: false }
		case 'uncovered':
			return { colorIndex: null, isStart: false }
		case 'outside':
			return null
	}
}

// MARK: Bottom bar

/**
 * How much weight the CTA carries. A step you can pass over reads as a
 * secondary action; anything that moves the trip forward is primary.
 */
export type CtaStyle = 'primary' | 'secondary'

export interface BottomBar {
	/** "Japan, Kyoto and Toronto" / "No places yet" */
	title: string
	/** "9 days · Mon, Aug 17 - 25, 2026 · CAD" / "Add places, or skip…" / "Starting on …" */
	subtitle: string
	/** "Next" / "Skip" / "Create Trip" */
	ctaTitle: string
	ctaStyle: CtaStyle
	/** Only the final step gates, on a 3+ character name — every other step is skippable, like iOS. */
	ctaEnabled: boolean
	/** The bar's own Back, shown when the step behind was skipped; the header chevron covers the rest. */
	showsBack: boolean
}

export const bottomBar = (draft: CreateTripDraft, step: CreateTripStep): BottomBar => {
	const hasPlaces = draft.destinations.length > 0
	const hasDate = draft.startDate !== null

	let subtitle: string
	if (hasPlaces) {
		subtitle = daysSummary(draft)
	} else if (step === 'where') {
		subtitle = 'Add places, or skip and add them later'
	} else {
		const start = startDateLabel(draft)
		subtitle = start ? `Starting on ${start}` : 'No starting date yet'
	}

	const title = hasPlaces ? joinedNames(draft) : 'No places yet'

	switch (step) {
		case 'where':
			return {
				title,
				subtitle,
				ctaTitle: hasPlaces ? 'Next' : 'Skip',
				ctaStyle: hasPlaces ? 'primary' : 'secondary',
				ctaEnabled: true,
				showsBack: false,
			}
		case 'when':
			return {
				title,
				subtitle,
				ctaTitle: hasDate ? 'Next' : 'Skip',
				ctaStyle: hasDate ? 'primary' : 'secondary',
				ctaEnabled: true,
				showsBack: !hasPlaces,
			}
		case 'name':
			return {
				title,
				subtitle,
				ctaTitle: 'Create Trip',
				ctaStyle: 'primary',
				ctaEnabled: tripName(draft).trim().length >= 2,
				showsBack: !hasPlaces || !hasDate,
			}
	}
}
